/**
 * DuplicatesReport — admin-only page that scans the entire patient database
 * for potential duplicates grouped by phone / email / name+dob, and lets
 * the admin merge them via the /patients/{target}/merge endpoint.
 *
 * Merge model (per user decision):
 *  - Admin explicitly picks the TARGET (survivor) via radio button.
 *  - All other patients in the group can be merged into the target one
 *    at a time. Merging reassigns treatments, invoices, prescriptions,
 *    consents, warranties, appointments, X-rays — nothing is lost.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, AlertTriangle, ArrowUpRight, Merge, Loader2, RefreshCw,
  Phone as PhoneIcon, Mail, Calendar as CalIcon, ShieldCheck, FileText, Clock,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const REASON_LABEL = { phone: "Same mobile", email: "Same email", "name+dob": "Same name + DOB" };
const REASON_TONE = {
  phone:      "bg-[#FDF3ED] text-[#8A3F1E]",
  email:      "bg-[#EEF1F5] text-[#28527A]",
  "name+dob": "bg-[#F5F0E4] text-[#7A5928]",
};

/* ---------------- Merge dialog ---------------- */
function MergeDialog({ group, onClose, onDone }) {
  const [targetId, setTargetId] = useState(group.patients[0]?.id);
  const [sourceId, setSourceId] = useState(group.patients[1]?.id);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doMerge = async () => {
    if (!targetId || !sourceId || targetId === sourceId) {
      return toast.error("Pick a different target and source");
    }
    if (!window.confirm(
      "Merging is irreversible. All treatments, invoices, prescriptions, " +
      "X-rays and appointments from the source will be moved to the target. " +
      "The source will be soft-deleted. Continue?"
    )) return;
    try {
      setBusy(true);
      const { data } = await api.post(`/patients/${targetId}/merge`, null, {
        params: { source_id: sourceId },
      });
      setResult(data);
      toast.success("Patients merged successfully");
    } catch (e) {
      toast.error(apiError(e, "Merge failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
         data-testid="merge-dialog" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#EFEADC] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F0EBDD] text-ink grid place-items-center">
            <Merge className="w-5 h-5" />
          </div>
          <div>
            <div className="overline text-ink-soft">Merge patients</div>
            <h2 className="font-display text-xl text-ink">Combine into one record</h2>
          </div>
        </div>

        {result ? (
          <div className="px-6 py-6">
            <div className="w-12 h-12 rounded-full bg-[#E8F0EB] text-[#2D6A4F] grid place-items-center mb-3">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="font-display text-xl text-ink">Merge complete</div>
            <div className="mt-2 text-sm text-ink-mid">
              Records reassigned:
              <ul className="mt-2 grid grid-cols-2 gap-1 text-xs">
                {Object.entries(result.records_reassigned || {}).map(([k, v]) => (
                  <li key={k} className="flex items-center gap-1.5 text-ink/80"><FileText className="w-3 h-3" />{k}: {v}</li>
                ))}
                {Object.keys(result.records_reassigned || {}).length === 0 &&
                  <li className="text-ink-soft">No linked records to transfer.</li>}
              </ul>
            </div>
            <button onClick={() => { onDone?.(); onClose(); }} data-testid="merge-done"
                    className="mt-6 btn-primary text-sm">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 text-xs text-ink-mid">
              Pick the <strong className="text-ink">survivor</strong> (target) and the record to fold into it.
            </div>
            <div className="max-h-[45vh] overflow-y-auto px-6 pb-4 space-y-2">
              {group.patients.map((p) => (
                <div key={p.id} className="rounded-xl border border-[#EFEADC] p-3 flex items-center gap-3">
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                    <input type="radio" name="target" value={p.id}
                           checked={targetId === p.id}
                           onChange={() => setTargetId(p.id)}
                           data-testid={`merge-target-${p.id}`}
                           className="accent-pine" />
                    <span className="text-xs text-ink-mid">Keep</span>
                  </label>
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                    <input type="radio" name="source" value={p.id}
                           checked={sourceId === p.id}
                           onChange={() => setSourceId(p.id)}
                           data-testid={`merge-source-${p.id}`}
                           className="accent-[#D06A42]" />
                    <span className="text-xs text-[#D06A42]">Merge</span>
                  </label>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">{p.name}</div>
                    <div className="text-xs text-ink-mid flex items-center gap-2 flex-wrap">
                      {p.phone && <span className="flex items-center gap-1"><PhoneIcon className="w-3 h-3" />{p.phone}</span>}
                      {p.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{p.email}</span>}
                      {p.dob && <span className="flex items-center gap-1"><CalIcon className="w-3 h-3" />{fmtDate(p.dob)}</span>}
                    </div>
                    <div className="text-[10px] text-ink-soft">Created {fmtDate(p.created_at)}</div>
                  </div>
                  <Link to={`/app/patients/${p.id}`} target="_blank"
                        className="text-[11px] text-ink hover:underline shrink-0 inline-flex items-center gap-1">
                    Open <ArrowUpRight className="w-3 h-3" />
                  </Link>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 bg-[#FBFAF6] border-t border-[#EFEADC] flex items-center justify-end gap-2">
              <button onClick={onClose} className="text-sm text-ink-mid hover:text-ink px-4 py-2">Cancel</button>
              <button onClick={doMerge}
                      data-testid="merge-confirm"
                      disabled={busy || targetId === sourceId || !targetId || !sourceId}
                      className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Merge className="w-4 h-4" />}
                Merge into selected
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Audit strip ---------------- */
function AuditStrip() {
  const [audits, setAudits] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/patients/duplicate-audits?limit=20")
       .then(({ data }) => setAudits(data.audits || []))
       .catch(() => {});
  }, []);
  if (audits.length === 0) return null;
  return (
    <div className="mt-8 rounded-2xl bg-white/70 border border-[#E7E1D3] p-5" data-testid="audit-strip">
      <button onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-ink" />
          <div>
            <div className="overline text-ink-soft">Duplicate-detection audit trail</div>
            <div className="font-display text-lg text-ink">{audits.length} recorded events</div>
          </div>
        </div>
        <span className="text-xs text-ink-mid">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-4 divide-y divide-[#EFEADC] text-sm">
          {audits.map((a) => (
            <div key={a.id} className="py-3 grid grid-cols-[auto_1fr_auto] gap-3 items-center">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                a.action === "blocked" ? "bg-[#FCECEC] text-[#B94A48]"
                : a.action === "merged" ? "bg-[#E8F0EB] text-[#2D6A4F]"
                : "bg-[#FDF3ED] text-[#8A3F1E]"}`}>
                {a.action}
              </span>
              <div className="min-w-0">
                <div className="text-xs text-ink/80 truncate">
                  Attempted: <strong className="text-ink">{a.attempted_name || "—"}</strong>
                  {a.attempted_phone && <> · {a.attempted_phone}</>}
                </div>
                <div className="text-[11px] text-ink-soft">
                  {a.actor_email} ({a.actor_role}) · {(a.match_reasons || []).join(", ") || "—"}
                </div>
              </div>
              <span className="text-[11px] text-ink-soft whitespace-nowrap">{fmtDate(a.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function DuplicatesReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mergeGroup, setMergeGroup] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data: d } = await api.get("/patients/duplicates-report");
      setData(d);
    } catch (e) {
      toast.error(apiError(e, "Failed to load report"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = data?.groups || [];
  const totals = data?.totals || {};

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                className="p-1" data-testid="duplicates-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <nav className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-soft mb-1" aria-label="Breadcrumb" data-testid="duplicates-breadcrumbs">
            <Link to="/app/settings" className="hover:text-ink transition-colors" data-testid="crumb-settings">Settings</Link>
            <span className="text-ink-disabled">/</span>
            <Link to="/app/settings#data-management" className="hover:text-ink transition-colors" data-testid="crumb-data-management">Data Management</Link>
            <span className="text-ink-disabled">/</span>
            <span className="text-ink-mid">Duplicate Patients</span>
          </nav>
          <h1 className="mt-2 font-display text-4xl tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-ink" /> Duplicate patients
          </h1>
          <p className="mt-2 text-sm text-ink-mid max-w-xl">
            Patients grouped by matching mobile, email, or name + date of birth. Merge duplicates to
            keep treatment history intact — nothing is lost.
          </p>
        </div>
        <button onClick={load} data-testid="btn-refresh-report"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E8EAE9] hover:bg-linen text-sm text-ink">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="dupe-report-stats">
        <StatPill label="Total dup groups" value={totals.total_groups ?? "—"} tone="pine" icon={AlertTriangle} />
        <StatPill label="By phone"         value={totals.phone_groups ?? "—"} tone="clay" icon={PhoneIcon} />
        <StatPill label="By email"         value={totals.email_groups ?? "—"} tone="pine" icon={Mail} />
        <StatPill label="By name + DOB"    value={totals.name_dob_groups ?? "—"} tone="pine" icon={CalIcon} />
      </div>

      {loading && (
        <div className="py-16 text-center text-sm text-ink-soft flex items-center justify-center gap-2"
             data-testid="report-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Scanning your patient database…
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div className="rounded-3xl bg-white/70 border border-[#E7E1D3] py-16 text-center"
             data-testid="report-empty">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#E8F0EB] grid place-items-center mb-3">
            <ShieldCheck className="w-6 h-6 text-[#2D6A4F]" />
          </div>
          <div className="font-display text-xl text-ink">Clean slate</div>
          <div className="text-sm text-ink-mid mt-1">No duplicate patients detected in your clinic. Well done.</div>
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div className="space-y-4" data-testid="dupe-groups">
          {groups.map((g, idx) => (
            <article key={`${g.reason}-${idx}`} className="rounded-2xl bg-white/85 border border-[#E7E1D3] p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${REASON_TONE[g.reason] || "bg-slate-100"}`}>
                      {REASON_LABEL[g.reason] || g.reason}
                    </span>
                    <span className="text-xs text-ink-soft font-mono truncate">{g.value}</span>
                  </div>
                  <div className="text-sm text-ink-mid mt-1">{g.count} patient records share this signal</div>
                </div>
                <button
                  onClick={() => setMergeGroup(g)}
                  data-testid={`merge-open-${idx}`}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pine text-linen text-xs font-medium hover:bg-[#264A3B]"
                >
                  <Merge className="w-3.5 h-3.5" /> Merge
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {g.patients.map((p) => (
                  <Link key={p.id} to={`/app/patients/${p.id}`}
                        className="rounded-xl border border-[#EFEADC] p-3 hover:border-pine/30 hover:bg-[#FBFAF6] transition-colors">
                    <div className="font-medium text-sm text-ink truncate">{p.name}</div>
                    <div className="text-xs text-ink-mid flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      {p.phone && <span>{p.phone}</span>}
                      {p.email && <span className="truncate">{p.email}</span>}
                      {p.dob && <span>DOB {fmtDate(p.dob)}</span>}
                    </div>
                    <div className="text-[10px] text-ink-soft mt-1">Created {fmtDate(p.created_at)}</div>
                  </Link>
                ))}
              </div>
            </article>
          ))}
        </div>
      )}

      <AuditStrip />

      {mergeGroup && (
        <MergeDialog
          group={mergeGroup}
          onClose={() => setMergeGroup(null)}
          onDone={load}
        />
      )}
    </motion.div>
  );
}

function StatPill({ label, value, tone, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-4">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full grid place-items-center ${
          tone === "clay" ? "bg-[#FDF3ED] text-[#D06A42]" : "bg-[#F0EBDD] text-ink"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="overline text-ink-soft">{label}</div>
      </div>
      <div className="font-display text-3xl leading-none mt-2 tabular-nums text-ink">{value}</div>
    </div>
  );
}
